import React, { useState } from 'react';
import {
  TextField,
  Button,
  Avatar,
  IconButton,
  Grid,
  Box,
} from '@mui/material';
import { PhotoCamera as PhotoCameraIcon, Add as AddIcon } from '@mui/icons-material';
import ExpertSidebar from '../../components/expert/ExpertSidebar';
import {
  PageLayout,
  MainContent,
  ContentContainer,
  SectionContainer,
  PageTitle,
  PageSubtitle,
  SectionTitle,
  FormGroup,
  FormActions,
  GridContainer,
  FlexContainer,
} from '../../styles/SharedStyles';
import styled from 'styled-components';
import {
  Card,
  CardContent,
  Typography,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PreviewPlanPage from '../../components/expert/PreviewPlanPage';
import { useUser } from '../../redux/slices/user/useUser';
import { useExpert } from '../../redux/slices/expert/useExpert';
import { updateExpert } from '../../redux/slices/expert/expertSlice';
import { useDispatch } from 'react-redux';
import * as triplinkService from '../../utils/triplinkService'
import { useAccessToken } from '../../redux/slices/accessToken/useAccessToken';
import { useBusiness } from '../../redux/slices/business/useBusiness';
import { useNavigate } from 'react-router-dom';

interface AgentProfile {
  name: string;
  title: string;
  bio: string;
  avatar: string;
  location: string;
  languages: string[];
  specialties: string[];
  recentDestinations: string[];
  contactLinks: {
    email: string;
    phone: string;
  };
  website: string;
  socialLinks: {
    instagram: string;
    facebook: string;
    linkedin: string;
  };
  meetingLink: string;
  collectionLink: string;
}

type SocialPlatform = keyof AgentProfile["socialLinks"];

type ContactLink = keyof AgentProfile["contactLinks"];

const PreviewContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const AvatarContainer = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  overflow: hidden;
`;

const AvatarOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }

  .MuiIconButton-root {
    color: white;
  }
`;

const MAX_IMAGE_SIZE_MB = 2
const MAX_IMAGE_SIZE_BYTE = MAX_IMAGE_SIZE_MB * 1024 * 1024

const AgentProfilePage: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const { body } = useUser();
  const { body: expertProfile } = useExpert();
  const { body: accessToken } = useAccessToken();
  const { body: business } = useBusiness();

  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState<string | null>(null);
  const [profile, setProfile] = useState<AgentProfile>({
    name: `${body?.first_name} ${body?.last_name}` || "",
    title: expertProfile?.title ?? "Trip Expert",
    bio: expertProfile?.description || "",
    avatar: expertProfile?.profile_image_url || "",
    location: expertProfile?.location || "",
    languages: ["English", "Spanish", "French"],
    specialties: ["Luxury Travel", "Adventure Tours", "Cultural Experiences"],
    recentDestinations: ["Maldives", "Switzerland", "Japan", "New Zealand"],
    contactLinks: {
      email: body?.email || "",
      phone: body?.phone || "+614xxxxxxxx",
    },
    website: expertProfile?.website ?? "",
    socialLinks: {
      instagram: expertProfile?.instagram_url?.split('/').at(-1) || "",
      facebook: expertProfile?.facebook_url?.split('/').at(-1) || "",
      linkedin: expertProfile?.linkedin_url?.split('/').at(-1) || "",
    },
    meetingLink: `https://triplink.vip/${expertProfile?.preferred_link}`,
    collectionLink: "https://triplink.vip/wanderlust/6f34ds",
  });

  const [editedProfile, setEditedProfile] = useState<AgentProfile>(profile);
  const [domainError, setDomainError] = useState(false);

  const handleSave = () => {
    setDomainError(false);

    const { bio, location, title, website, avatar } = editedProfile

    // Check if the domain has the valid pattern
    const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/
    if (website.length > 0 && !domainRegex.test(website)) {
      setDomainError(true)
      return
    }

    const expertUpdates = {
      description: bio,
      location: location,
      title: title,
      website: website,
      instagram_url: editedProfile.socialLinks.instagram ? `https://www.instagram.com/${editedProfile.socialLinks.instagram}` : null,
      facebook_url: editedProfile.socialLinks.facebook ? `https://www.facebook.com/${editedProfile.socialLinks.facebook}` : null,
      linkedin_url: editedProfile.socialLinks.linkedin ? `https://www.linkedin.com/in/${editedProfile.socialLinks.linkedin}` : null,
      // README: Add more attributes as needed
    }

    dispatch(updateExpert({
      updates: expertUpdates,
      requireAccessToken: true,
    }))

    setProfile(editedProfile);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setDomainError(false);
    setEditedProfile(profile);
    setIsEditing(false);
  };

  const handleCopyLink = async (type: "meeting" | "collection") => {
    const link =
      type === "meeting" ? profile.meetingLink : profile.collectionLink;
    try {
      await navigator.clipboard.writeText(link);
      setShowCopySuccess(type);
      setTimeout(() => setShowCopySuccess(null), 2000);
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  const handleSocialLinkChange = (platform: SocialPlatform, value: string) => {
    setEditedProfile({
      ...editedProfile,
      socialLinks: {
        ...editedProfile.socialLinks,
        [platform]: value,
      },
    });
  };

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file && file.size > MAX_IMAGE_SIZE_BYTE) {
      alert(`Image is too large, Please upload a file smaller than ${MAX_IMAGE_SIZE_MB}MB`)
      return
    }

    if (file && accessToken) {
      try {
        setUploading(true); // Get started with uploading avatar image
        const publicUrl = await triplinkService.uploadExpertAvatar(accessToken, file)
        setUploading(false); // Uploading is finished

        setEditedProfile({
          ...editedProfile,
          avatar: publicUrl
        })
      } catch (error) {
        alert(`Failed to upload expert profile image: ${error}`)
      }
    }
  };

  const handleContactLinkChange = (platform: ContactLink, value: string) => {
    setEditedProfile({
      ...editedProfile,
      contactLinks: {
        ...editedProfile.contactLinks,
        [platform]: value,
      },
    });
  };

  const renderEditComponent = () => {
    if (isEditing) {
      return (
        <Box>
          <IconButton onClick={handleSave} size="small" color="primary" disabled={uploading}>
            <SaveIcon />
          </IconButton>
          <IconButton onClick={handleCancel} size="small" color="error" disabled={uploading}>
            <CancelIcon />
          </IconButton>
        </Box>
      )
    } else {
      return (
        <IconButton onClick={() => setIsEditing(true)} size="small">
          <EditIcon />
        </IconButton>
      )
    }
  }

  const handlePublish = () => {
    /**
     * TODO
     * Implement the function to update the expert profile status including backend api development
     */
  }

  const handleUpgrade = () => {
    /**
     * TODO
     */
    navigate('/expert/billing')
  }

  return (
    <PageLayout>
      <ExpertSidebar />
      <MainContent>
        <ContentContainer>
          <Box display="flex" justifyContent="space-between" alignItems={"center"}>
            <PageTitle variant="h4" fontWeight={600} gutterBottom>
              My Profile ({expertProfile?.status})
            </PageTitle>
            <Button
              variant="contained"
              onClick={handlePublish}
              style={{
                textTransform: 'none',
              }}>
              Publish
            </Button>
          </Box>
          <SectionContainer>
            <FlexContainer style={{ justifyContent: 'center', alignItems: "center", gap: 50, padding: 20 }}>
              <AvatarContainer>
                <Avatar
                  src={`${editedProfile.avatar}?t=${Date.now()}`}
                  style={{
                    width: '100%',
                    height: '100%'
                  }} />
                {isEditing && (
                  <AvatarOverlay>
                    <input
                      accept="image/*"
                      type="file"
                      id="avatar-upload"
                      onChange={handleAvatarChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="avatar-upload">
                      <IconButton component="span">
                        <PhotoCameraIcon />
                      </IconButton>
                    </label>
                  </AvatarOverlay>
                )}
              </AvatarContainer>
              {isEditing ? (
                <Box display="flex" flexDirection="column">
                  <TextField
                    value={editedProfile.name}
                    variant="standard"
                    onChange={(e) => setEditedProfile({ ...editedProfile, name: e.target.value })}
                    sx={{ mt: 2, textAlign: "center" }}
                  />
                  <TextField
                    value={editedProfile.title}
                    variant="standard"
                    onChange={(e) => setEditedProfile({ ...editedProfile, title: e.target.value })}
                    sx={{ mt: 2, textAlign: "center" }}
                  />
                </Box>
              ) : (

                <Box>
                  <Typography variant="h6" align="center" sx={{ mt: 2 }}>
                    {profile.name}
                  </Typography>
                  <Typography variant="subtitle1" color="textSecondary" align="center">
                    {profile.title}
                  </Typography>
                </Box>
              )}
            </FlexContainer>
          </SectionContainer>

          <SectionContainer>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">About</Typography>
                  {renderEditComponent()}
                </Box>
                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={editedProfile.bio}
                    onChange={(e) => setEditedProfile({ ...editedProfile, bio: e.target.value })}
                    variant="outlined"
                    placeholder="Tell us about yourself..."
                  />
                ) : (
                  <Typography paragraph>{profile.bio}</Typography>
                )}
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Location
                  </Typography>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      value={editedProfile.location}
                      onChange={(e) => setEditedProfile({ ...editedProfile, location: e.target.value })}
                      variant="outlined"
                      size="small"
                      placeholder="e.g., Sydney, Australia"
                    />
                  ) : (
                    <Typography>{profile.location}</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </SectionContainer>

          {!business && <SectionContainer>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Display Links</Typography>
                  {renderEditComponent()}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Website"
                      value={editedProfile.website}
                      disabled={!isEditing}
                      onChange={(e) => {
                        setEditedProfile({ ...editedProfile, website: e.target.value })
                      }}
                      error={domainError}
                      helperText={domainError ? 'Please enter a valid domain like example.com' : ''}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  {(
                    Object.entries(profile?.socialLinks) as [
                      SocialPlatform,
                      string
                    ][]
                  ).map(([platform, handle]) => (
                    <Grid item xs={12} sm={8} key={platform}>
                      <TextField
                        fullWidth
                        label={
                          platform.charAt(0).toUpperCase() + platform.slice(1)
                        }
                        value={
                          isEditing ? editedProfile?.socialLinks[platform] : handle
                        }
                        onChange={(e) =>
                          handleSocialLinkChange(platform, e.target.value)
                        }
                        disabled={!isEditing}
                        variant="outlined"
                        size="small"
                        placeholder={`Enter your ${platform} handle`}
                        InputProps={{
                          startAdornment: `https://www.${platform}.com/${platform === 'linkedin' ? 'in/' : ''}`
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </SectionContainer>}

          <Box display={"flex"} justifyContent={"end"} marginBottom={2}>
            <Button
              variant="contained"
              onClick={handlePublish}
              style={{
                textTransform: 'none',
              }}
            >
              Publish
            </Button>
          </Box>

          <SectionContainer>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  My Triplink
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={profile.meetingLink}
                        InputProps={{ readOnly: true }}
                      />
                      <IconButton
                        onClick={() => handleCopyLink("meeting")}
                        color={
                          showCopySuccess === "meeting" ? "success" : "default"
                        }
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
                {!business && <Box marginY={2}>
                  <Button
                    variant="contained"
                    onClick={handleUpgrade}
                    style={{
                      textTransform: 'none',
                      marginBottom: '10px',
                    }}
                  >
                    Enable custom domain (Business plan)
                  </Button>
                  <Typography>Eg. triplink.vip/yourbusiness</Typography>
                </Box>}
              </CardContent>
            </Card>
          </SectionContainer>

          <SectionContainer>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">
                    Private Contact (Not Displayed)
                  </Typography>
                  {renderEditComponent()}
                </Box>
                <Grid container spacing={2}>
                  {(
                    Object.entries(profile.contactLinks) as [
                      ContactLink,
                      string
                    ][]
                  ).map(([platform, handle]) => (
                    <Grid item xs={12} sm={6} key={platform}>
                      <TextField
                        fullWidth
                        label={
                          platform.charAt(0).toUpperCase() + platform.slice(1)
                        }
                        value={
                          isEditing
                            ? editedProfile.contactLinks[platform]
                            : handle
                        }
                        onChange={(e) =>
                          handleContactLinkChange(platform, e.target.value)
                        }
                        disabled={true}
                        variant="outlined"
                        size="small"
                        placeholder={`Enter your ${platform} handle`}
                        InputProps={{
                          startAdornment: platform === "email" ? "" : "",
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </SectionContainer>
        </ContentContainer>
      </MainContent>
      <PreviewContainer>
        <PreviewPlanPage expertUrl={expertProfile?.preferred_link} />
      </PreviewContainer>
    </PageLayout>
  );
};

export default AgentProfilePage;
